import { useDispatch, useSelector } from 'react-redux';
import { Status } from '@sunbit-dev/purchase-common/src/enums/guided-app/Status';
import { loanApplicationNetworkService } from '~/services/LoanApplicationNetworkService';
import { setStatus } from '~/store/reducers/purchase/purchase';
import * as purchaseStore from '~/store/reducers/purchase/purchase';
import { useCallback, useEffect, useRef } from 'react';
import { addError } from '~/store/reducers/errors/errors';
import { EventsNames } from '~/enums/Events';
import { convertEventToError } from '@sunbit-dev/purchase-common/src/enums/utils';
import { RequirementName } from '~/models/WorkflowState';
import { isRequirementPending } from '~/utilities/workflowState/workflowState';
import { retryWithDelay } from '~/utils/retryWithDelay';

const MAX_POLLING_RETRIES = 3;
const POLLING_INTERVAL = 1500;

interface PollStatusProps {
  targetStatusActions: Partial<Record<Status, () => void>>;
  awaitRequirementCompletion?: RequirementName;
}

export const useUpdateStatus = () => {
  const dispatch = useDispatch();
  const { offer, purchaseInfo } = useSelector(purchaseStore.getStore);

  return useCallback(
    async ({ status }: { status: Status }): Promise<void> => {
      try {
        await retryWithDelay(
          () =>
            loanApplicationNetworkService.updateApplicationStatus({
              offerId: offer?.offerId,
              status,
              loanApplicationId: purchaseInfo?.loanApplicationId,
            }),
          3,
          1000
        );
        await dispatch(setStatus(status));
      } catch {}
    },
    [purchaseInfo?.loanApplicationId, offer?.offerId]
  );
};

export const usePollStatus = () => {
  const dispatch = useDispatch();
  const { purchaseInfo } = useSelector(purchaseStore.getStore);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const pollStatus = useCallback(
    async ({ targetStatusActions, awaitRequirementCompletion }: PollStatusProps) => {
      let retryCount = 0;

      const poll = async () => {
        try {
          const { status, _workflowState } = await loanApplicationNetworkService.getApplicationStatus({
            loanApplicationId: purchaseInfo?.loanApplicationId,
          });

          const targetAction = targetStatusActions[status];

          if (targetAction) {
            if (awaitRequirementCompletion && isRequirementPending(_workflowState, awaitRequirementCompletion)) {
              timeoutRef.current = setTimeout(poll, POLLING_INTERVAL);
              return;
            }

            clearTimeout(timeoutRef.current);
            targetAction();
          } else {
            timeoutRef.current = setTimeout(poll, POLLING_INTERVAL);
          }
        } catch (error) {
          retryCount++;

          if (retryCount >= MAX_POLLING_RETRIES) {
            clearTimeout(timeoutRef.current);
            dispatch(addError({ eventName: convertEventToError(EventsNames.getApplicationStatus), payload: error }));
          } else {
            timeoutRef.current = setTimeout(poll, POLLING_INTERVAL);
          }
        }
      };

      await poll();
    },
    [purchaseInfo?.loanApplicationId]
  );

  return { pollStatus };
};
